// 화면 18 — Leader Stocks 45 Grid (Phase B) 타입.
// 권위:
//   - docs/ARCH_DASHBOARD.md v1.10.3 §4.18 / §6.2 (라우트 /leading/stocks, 폴링 300s, WebSocket 미사용)
//   - docs/QUANT_SPEC.md §2.6.1.1 (O-DB-9: In/Out Diff 산식, NEW_IN 임계 N=1 only NORMAL≤8 / WATCH 9~15 / ALERT≥16)
//   - docs/QUANT_SPEC.md §2.6.4.1 (O-DB-10: 밴드 이동 타임라인 dense + Top45 마커)
//
// 백엔드 DTO snake_case. envelope.data 만 axios interceptor 가 언랩.
//
// v1.10.3 변경:
//   - LeaderStocksSnapshot.config_version 제거 (응답에서 사라짐)
//   - LeaderStocksDiff.lookback_label (문자열) → lookback_days (정수 1/5/30)
//   - BandHistoryRow: in_top45 추가, rank_in_sector / sector nullable (Top45 미진입 일자는 null)

export type Band = 'A' | 'B' | 'C' | null;

// ── /api/v1/leading-stocks/daily 응답 ───────────────────────────
export interface LeaderStockRow {
  sector: string;
  rank_in_sector: number;
  symbol: string;
  name: string;
  final_score: number | null;
  perf_score: number | null;
  soft_rank: number | null;
  volume_rank: number | null;
  final_score_percentile: number | null;
  band: Band;
}

export interface LeaderStocksSnapshot {
  trading_date: string;
  run_id: string | null;
  pipeline_mode: 'today_close' | 'next_day_pre_open' | null;
  leader_stocks: LeaderStockRow[];
  as_of: string | null;
}

// ── /api/v1/leading-stocks/diff 응답 ────────────────────────────
// UI 토글 라벨 — 표시용. 백엔드 요청 / 응답은 정수 days (1/5/30) 만 사용.
export type LookbackLabel = 'D-1' | '5d' | '30d';
export type LookbackDays = 1 | 5 | 30;

export const LOOKBACK_DAYS: readonly LookbackDays[] = [1, 5, 30] as const;

export function daysToLabel(days: number): LookbackLabel {
  if (days <= 1) return 'D-1';
  if (days <= 5) return '5d';
  return '30d';
}

export function labelToDays(label: LookbackLabel): LookbackDays {
  switch (label) {
    case 'D-1':
      return 1;
    case '5d':
      return 5;
    case '30d':
      return 30;
  }
}

export interface DiffEntry {
  symbol: string;
  name: string | null;
  sector: string;
  rank_in_sector: number;
  final_score: number | null;
  band: Band;
}

export interface HeldEntry {
  symbol: string;
  name: string | null;
  sector_today: string;
  sector_prev: string;
  rank_today: number;
  rank_prev: number;
  final_score_today: number | null;
  final_score_prev: number | null;
  band_today: Band;
  band_prev: Band;
}

export interface LeaderStocksDiff {
  trading_date: string;
  prev_trading_date: string;
  lookback_days: number; // v1.10.3: 정수 (1 | 5 | 30)
  new_in: DiffEntry[];
  dropped_out: DiffEntry[];
  held: HeldEntry[];
  counts: {
    new_in: number;
    dropped_out: number;
    held: number;
  };
}

// ── /api/v1/leading-stocks/{symbol}/band-history 응답 ───────────
// v1.10.3: 1차 소스가 stock_daily_score (dense) 로 변경.
//   - rows 는 days 영업일 수 전체 (Top45 진입 일자 + 미진입 일자 모두).
//   - in_top45 = true 인 일자만 rank_in_sector / sector 가 유효 (그 외 null).
export interface BandHistoryRow {
  trading_date: string;
  band: Band;
  in_top45: boolean;
  rank_in_sector: number | null;
  sector: string | null;
  final_score: number | null;
}

export interface BandHistory {
  symbol: string;
  name: string;
  days: number;
  rows: BandHistoryRow[];
}

// ── /api/v1/stocks/{symbol}/ohlcv 응답 ──────────────────────────
// 캔들 차트용. interval 별 days cap: daily 365 / weekly 1095 / monthly 1825.
// 백엔드는 trading_date ASC 정렬을 보장.
export type OhlcvInterval = 'daily' | 'weekly' | 'monthly';

export interface OhlcvRow {
  trading_date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface StockOhlcv {
  symbol: string;
  name: string;
  interval: OhlcvInterval;
  days: number;
  rows: OhlcvRow[];
}

// 캔들 차트 기간 컨트롤 — interval 별 chip 단계 (idx 동기화로 toggle 시 자동 매핑).
export const OHLCV_STEPS: Record<OhlcvInterval, readonly number[]> = {
  daily: [30, 60, 90, 180, 365],
  weekly: [12, 26, 52, 104, 156],
  monthly: [6, 12, 24, 36, 60],
} as const;

export const OHLCV_DEFAULT_IDX = 1; // daily=60, weekly=26, monthly=12

// 캔들 차트 lazy-load cap — interval 별 백엔드 days 한계 (백엔드 정책과 일치).
// 좌측 viewport 확장 fetch 가 이 값에 도달하면 더 이상 증가시키지 않는다.
export const DAYS_CAP: Record<OhlcvInterval, number> = {
  daily: 365,
  weekly: 1095,
  monthly: 1825,
};

export function ohlcvStepLabel(interval: OhlcvInterval, value: number): string {
  switch (interval) {
    case 'daily':
      return `${value}일`;
    case 'weekly':
      return `${value}주`;
    case 'monthly':
      return `${value}개월`;
  }
}

// ── 밴드 색상 매핑 ──────────────────────────────────────────────
// A = 강세 (mark-up), B = 중립 (warn / amber), C = 약세 (mark-down) — 가격 상승/하락 색 톤 재사용.
// null (unclassified) 은 회색.
export function bandColor(b: Band): string {
  switch (b) {
    case 'A':
      return 'var(--mark-up)';
    case 'B':
      return 'var(--warn)';
    case 'C':
      return 'var(--mark-down)';
    default:
      return 'var(--ink-faint)';
  }
}

export function bandSoftBg(b: Band): string {
  switch (b) {
    case 'A':
      return 'var(--mark-up-soft)';
    case 'B':
      return 'var(--warn-soft)';
    case 'C':
      return 'var(--mark-down-soft)';
    default:
      return 'var(--inset)';
  }
}

// O-DB-9 NEW_IN 임계 (N=1 only — lookback_days = 1 일 때만 적용).
//   NORMAL: |NEW_IN| ≤ 8
//   WATCH:  9 ≤ |NEW_IN| ≤ 15
//   ALERT:  |NEW_IN| ≥ 16
export type NewInState = 'NORMAL' | 'WATCH' | 'ALERT';

export function classifyNewInState(count: number, days: number): NewInState | null {
  // 임계는 D-1 (days=1) 에만 적용. 5d / 30d 는 보조선 없음.
  if (days !== 1) return null;
  if (count >= 16) return 'ALERT';
  if (count >= 9) return 'WATCH';
  return 'NORMAL';
}

// 기준일 ↔ prev 일자 유틸 (단순 캘린더 차감 — 실제 영업일 매핑은 백엔드 책임).
export function lookbackToPrevHint(date: string, days: number): string {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return date;
  d.setDate(d.getDate() - days);
  return toYmd(d);
}

export function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function todayYmd(): string {
  return toYmd(new Date());
}
